import * as React from 'react';
import { cn } from '@/lib/utils/classNames';

/**
 * GGC V2. Linked Text — Figma node 166:381 family (Component 03).
 *
 * Inline link styled with the CTA blue color and an underline. Used in
 * tables for influencer/campaign/payment row references.
 *
 * Spec: Inter Regular 13px, color guide-blue (#3453a8), text-decoration underline.
 *
 * Renders a real `<a>` when `href` is given (real navigation — supports
 * middle-click/cmd-click "open in new tab", shows the target in the status
 * bar, is crawlable). Without `href` it renders a `<button type="button">`
 * instead, for callers that only want the link visual for an in-page action
 * (expand/collapse, open a dialog, toggle a filter) — an `<a>` with no
 * `href` isn't a real link (not keyboard-focusable by default, no browser
 * link behavior, and Lighthouse flags it as a non-crawlable anchor), so a
 * button is the semantically correct element for that case.
 */
export type GgcLinkedTextProps =
  | ({ href: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>)
  | ({ href?: undefined } & React.ButtonHTMLAttributes<HTMLButtonElement>);

const GgcLinkedText = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, GgcLinkedTextProps>(
  ({ className, children, ...props }, ref) => {
    if (props.href) {
      const { href, ...anchorProps } = props as { href: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={cn('ggc-linked-text', className)}
          {...anchorProps}
        >
          {children}
        </a>
      );
    }

    const { href: _href, ...buttonProps } = props as { href?: undefined } & React.ButtonHTMLAttributes<HTMLButtonElement>;
    return (
      <button
        type="button"
        ref={ref as React.Ref<HTMLButtonElement>}
        className={cn('ggc-linked-text', className)}
        {...buttonProps}
      >
        {children}
      </button>
    );
  }
);
GgcLinkedText.displayName = 'GgcLinkedText';

export { GgcLinkedText };
